import { ArrowLeftRight, FlaskConical, ShieldAlert } from "lucide-react";
import { useId, useState, type KeyboardEvent } from "react";

import {
  classifyComparisonFailure,
  useComparePreviewRunsMutation,
} from "@/api/previewComparison";
import { useCapability } from "@/auth/SessionContext";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/format";
import {
  CANDIDATE_AXIS,
  COMPARAND_REFUSAL_SENTENCES,
  COMPARE_GROUPS,
  COMPARE_GROUP_LABELS,
  CONFOUNDER_LABELS,
  CONFOUNDER_SCOPE_LABELS,
  CONFOUNDER_SENTENCES,
  CONTROL_LABELS,
  CONTROL_SENTENCES,
  CONTROL_TONES,
  EVENT_KIND_LABELS,
  FIELD_LABELS,
  IDENTITY_AXIS_LABELS,
  INPUT_SCRIPT_RELATION_LABELS,
  INPUT_SCRIPT_RELATION_SENTENCES,
  ORDERING_LABELS,
  OUTCOME_LABELS,
  OUTCOME_TONES,
  UNALIGNABLE_REASON_LABELS,
  UNALIGNABLE_REASON_SENTENCES,
  UNATTESTED,
  WITHHELD_FIELD,
  WITHHELD_SENTENCE,
  compareRequestBody,
  controlOf,
  describeAlignmentKey,
  describeCounts,
  describeTotals,
  promptBodiesLoadable,
  formatRecordedValue,
  groupAlignment,
  groupIsEmpty,
  hasUnopenableBodyDifference,
  plural,
  readComparand,
  readComparison,
  summaryAlignment,
  summaryCallTypes,
  unkeyedUnalignable,
  UNOPENABLE_BODY_SENTENCE,
  type CompareDirection,
  type CompareGroupId,
  type GroupAlignment,
  type PreviewRunAlignedEvent,
  type PreviewRunComparisonResult,
  type PreviewRunComparisonSide,
  type PreviewRunEventDifference,
  type PreviewRunUnalignableEvent,
} from "@/lib/previewComparison";
import {
  CAPTURE_STATE_LABELS,
  CONTENT_UNAVAILABLE_LABELS,
  candidateDisplayVersion,
} from "@/lib/previewInsights";
import { closestDirection, horizontalStep } from "@/lib/textDirection";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/ui/Card";
import { Input, Label } from "@/ui/Input";
import { Notice } from "@/ui/Notice";
import { ComparePromptBodies } from "@/ui/preview/ComparePromptBodies";
import { PreviewRunReadFailure } from "@/ui/preview/PreviewRunReadFailure";
import { EmptyState } from "@/ui/states";

import { Fact, Identifier } from "@/routes/previewRunParts";

/**
 * Preview Insights — the Compare tab (#1107, approved design §10.2 tab 4 and
 * §11.3).
 *
 * Two authorized runs of ONE Project, side by side: what each recorded at the
 * same coordinate, what only one of them holds, and every way the experiment
 * was not controlled. The comparison itself is #1106's — aligned on stable
 * coordinates, typed as `changed`, `added`, `removed` or `unalignable`, and
 * computed for the request and dropped with it. This surface renders it.
 *
 * ## Selecting the second run
 *
 * The page's own run is one side. The other is named by its opaque locator,
 * the same value the run page's address carries and the only thing this service
 * publishes about a run that a clinician can carry from one page to another —
 * there is no Project-wide run listing to pick from, by design (a listing would
 * be a second way to learn which runs exist). The browser refuses exactly two
 * things without asking: an empty box, and the run already on screen. Every
 * other judgement — another Project's run, another environment's, a revoked
 * binding, an unknown locator — is the resolver's, and all of them arrive as
 * the ONE generic not-found this surface renders as one generic sentence.
 *
 * Which run is the BASE is a control, because `added`/`removed` and every
 * `base` value are computed against the direction that was sent. Swapping
 * re-asks rather than re-labelling.
 *
 * ## What is on screen at all times, and cannot be dismissed
 *
 * The server's fixed disclaimer, the controlled/confounded label, and every
 * confounder and incomplete-capture warning it reported sit ABOVE the group
 * tabs — in the panel, not in a tab — so navigating from Conversation to Safety
 * cannot leave them behind. There is no dismiss, hide or collapse control on
 * any of them: a clinician must not be able to reach a clean-looking comparison
 * that is not one. Nothing is a toast, either; a warning that scrolls away on a
 * timer is a warning that can be missed.
 *
 * ## What this surface never does
 *
 * It declares no winner, recommends no candidate, and infers no cause — the
 * server's vocabulary carries none, and nothing here composes one. It computes
 * no difference: every `changed`, every differing field and every confounder is
 * a typed member of the response. Prompt bodies are never part of it; the
 * comparison is body-free by construction, and the two texts behind two
 * different digests load only when a clinician asks for one pair
 * (`ComparePromptBodies`).
 */
export function PreviewRunCompare({
  projectId,
  previewRunRef,
}: {
  projectId: string;
  previewRunRef: string;
}) {
  const inspect = useCapability("preview.inspect");
  const [typed, setTyped] = useState("");
  const [direction, setDirection] = useState<CompareDirection>("this_run_is_base");
  const compare = useComparePreviewRunsMutation(projectId);
  const headingId = useId();
  const fieldId = useId();
  const refusalId = useId();

  const { ref: otherRef, refusal } = readComparand(typed, previewRunRef);

  function submit(next: CompareDirection = direction) {
    if (refusal !== null || !inspect.allowed) {
      return;
    }
    compare.mutate(compareRequestBody(previewRunRef, otherRef, next));
  }

  /**
   * A HELD RESULT IS ONLY EVER SHOWN BESIDE THE REQUEST IT ANSWERS.
   *
   * `added`, `removed` and every `base` value are computed against the pair and
   * the direction that were SENT, so a result left on screen while the form
   * describes a different pair or the opposite direction inverts its reading
   * (#1128 review, finding 1). Whenever the form stops describing the request
   * the mutation holds, the result is dropped — the surface stops answering a
   * question nobody is asking rather than answering it wrongly.
   */
  function dropResultIfStale(nextTyped: string, nextDirection: CompareDirection) {
    const held = compare.variables;
    if (held === undefined) {
      return;
    }
    const wanted = compareRequestBody(
      previewRunRef,
      readComparand(nextTyped, previewRunRef).ref,
      nextDirection,
    );
    if (
      wanted.basePreviewRunRef !== held.basePreviewRunRef ||
      wanted.comparedPreviewRunRef !== held.comparedPreviewRunRef
    ) {
      compare.reset();
    }
  }

  function changeComparand(value: string) {
    setTyped(value);
    dropResultIfStale(value, direction);
  }

  function swap() {
    const next: CompareDirection =
      direction === "this_run_is_base" ? "this_run_is_compared" : "this_run_is_base";
    setDirection(next);
    if (compare.status === "idle") {
      return;
    }
    if (refusal === null) {
      // Re-ask rather than re-label: the answer to the other direction is a
      // different computation, not a different presentation of this one.
      submit(next);
    } else {
      // The direction changed and there is nothing to ask with, so the held
      // result no longer answers what the form says.
      dropResultIfStale(typed, next);
    }
  }

  return (
    <Card role="region" aria-labelledby={headingId}>
      <CardHeader>
        <CardTitle id={headingId}>Compare with another run</CardTitle>
      </CardHeader>
      <CardBody className="space-y-5">
        <p className="text-sm text-muted-foreground">
          Compare this run with another preview run of this project. Both runs
          are read under your own authorization; the comparison is computed for
          the request and is not stored.
        </p>

        <form
          className="space-y-3"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1 space-y-1.5">
              <Label htmlFor={fieldId}>Other run locator</Label>
              <Input
                id={fieldId}
                value={typed}
                // LTR inside a page that may be running right to left: a
                // locator is a machine identifier and reads the same either
                // way, so its box does not mirror.
                dir="ltr"
                autoComplete="off"
                spellCheck={false}
                placeholder="pvs_…"
                disabled={!inspect.allowed}
                // ALWAYS associated: the line below is either why the box
                // cannot be sent or what the current direction means, and both
                // are things a screen-reader user needs when the box has focus.
                aria-describedby={refusalId}
                aria-invalid={refusal === "same_run" ? true : undefined}
                onChange={(event) => changeComparand(event.target.value)}
              />
            </div>
            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={
                  !inspect.allowed || refusal !== null || compare.isPending
                }
              >
                {compare.isPending ? "Comparing…" : "Compare"}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={swap}
                disabled={!inspect.allowed || compare.isPending}
              >
                <ArrowLeftRight aria-hidden="true" className="h-4 w-4" />
                Swap direction
              </Button>
            </div>
          </div>

          <p
            id={refusalId}
            className={cn(
              "text-sm",
              refusal === "same_run" ? "text-destructive" : "text-muted-foreground",
            )}
          >
            {refusal !== null && typed.length > 0
              ? COMPARAND_REFUSAL_SENTENCES[refusal]
              : direction === "this_run_is_base"
                ? "This run is the base: an event only the other run holds is reported as added."
                : "The other run is the base: an event only this run holds is reported as added."}
          </p>
        </form>

        {!inspect.allowed ? (
          <Notice tone="warning">
            Comparing preview runs needs the “preview.inspect” permission, which
            your account does not hold. Nothing has been read.
          </Notice>
        ) : null}

        {compare.isError ? (
          <ComparisonFailure
            failure={classifyComparisonFailure(compare.error)}
            error={compare.error}
            onRetry={() => submit()}
          />
        ) : null}

        {compare.isSuccess && compare.data ? (
          <ComparisonResult
            response={compare.data}
            projectId={projectId}
            thisRunRef={previewRunRef}
          />
        ) : null}
      </CardBody>
    </Card>
  );
}

// ── The result ───────────────────────────────────────────────────────────────

function ComparisonResult({
  response,
  projectId,
  thisRunRef,
}: {
  response: Parameters<typeof readComparison>[0];
  projectId: string;
  thisRunRef: string;
}) {
  const reading = readComparison(response);
  const [group, setGroup] = useState<CompareGroupId>("conversation");
  const [shown, setShown] = useState<ReadonlySet<CompareGroupId>>(
    () => new Set<CompareGroupId>(["conversation"]),
  );
  const tablistId = useId();

  function select(next: CompareGroupId) {
    setShown((current) =>
      current.has(next) ? current : new Set([...current, next]),
    );
    setGroup(next);
  }

  if (reading.kind === "unreadable") {
    // BOTH RUNS RESOLVED AND ONE HOLDS NOTHING THIS DEPLOYMENT CAN READ. A 200
    // with an empty slot, never a not-found: the contract refuses to collapse a
    // run that exists onto the answer a run that never existed gets. Each side
    // says which state it is in, in the six-value vocabulary that never
    // collapses `absent` onto `pending` onto `failed`.
    return (
      <div className="space-y-4">
        <RunPair
          base={reading.base}
          compared={reading.compared}
          thisRunRef={thisRunRef}
        />
        <EmptyState
          title="These two runs cannot be compared"
          description={reading.unreadable
            .map(
              ({ side, run }) =>
                `The ${side} run holds no readable trace: ${CAPTURE_STATE_LABELS[run.run.captureState]}${
                  run.run.contentUnavailableReason
                    ? ` — ${CONTENT_UNAVAILABLE_LABELS[run.run.contentUnavailableReason]}`
                    : ""
                }.`,
            )
            .join(" ")}
        />
      </div>
    );
  }

  const { result } = reading;
  const control = controlOf(result);

  return (
    <div className="space-y-4">
      <RunPair
        base={reading.base}
        compared={reading.compared}
        thisRunRef={thisRunRef}
      />

      {/*
        THE SERVER'S OWN SENTENCE, rendered verbatim and never composed from
        content. It states what a comparison is and is not, and it is the only
        place on this surface where causality is spoken about at all.
      */}
      <p
        data-slot="comparison-disclaimer"
        className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-4 py-3 text-sm font-medium text-foreground"
      >
        <FlaskConical aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0" />
        <span>{result.disclaimer}</span>
      </p>

      {/*
        The control label and every confounder, ABOVE the tab strip so they are
        on screen for every group. No dismiss, no collapse: a clinician must not
        be able to reach a clean-looking comparison that is not one.
      */}
      <div
        className={cn(
          "space-y-3 rounded-md border px-4 py-3",
          control === "confounded"
            ? "border-warning/60 bg-warning/5"
            : "border-border bg-muted/30",
        )}
      >
        <div className="flex flex-wrap items-start gap-3">
          <Badge tone={CONTROL_TONES[control]}>{CONTROL_LABELS[control]}</Badge>
          <p className="min-w-0 flex-1 text-sm text-foreground">
            {CONTROL_SENTENCES[control]}
          </p>
        </div>

        {result.confounders.length > 0 ? (
          <ul className="space-y-2" aria-label="Confounders reported by the Editor Service">
            {result.confounders.map((confounder) => (
              <li
                key={`${confounder.kind}:${confounder.scope}`}
                className="flex items-start gap-2 rounded-md border border-warning/40 bg-card px-3 py-2 text-sm"
              >
                <ShieldAlert
                  aria-hidden="true"
                  className="mt-0.5 h-4 w-4 shrink-0 text-warning-foreground"
                />
                <div className="min-w-0 space-y-1">
                  <p className="font-medium text-foreground">
                    {CONFOUNDER_LABELS[confounder.kind]}
                  </p>
                  <p className="text-muted-foreground">
                    {CONFOUNDER_SENTENCES[confounder.kind]}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {CONFOUNDER_SCOPE_LABELS[confounder.scope]}
                  </p>
                  <ConfounderValues confounder={confounder} />
                </div>
              </li>
            ))}
          </ul>
        ) : null}

        <p className="text-sm text-muted-foreground">{describeTotals(result.events.counts)}</p>
      </div>

      <UnkeyedUnalignable events={unkeyedUnalignable(result)} />

      <div>
        <div
          role="tablist"
          aria-label="Comparison groups"
          className="flex overflow-x-auto border-b border-border"
        >
          {COMPARE_GROUPS.map((id) => (
            <button
              key={id}
              type="button"
              role="tab"
              id={`${tablistId}-${id}`}
              aria-selected={group === id}
              aria-controls={`${tablistId}-${id}-panel`}
              tabIndex={group === id ? 0 : -1}
              className={cn(
                "-mb-px shrink-0 border-b-2 px-3 py-2 text-sm whitespace-nowrap focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
                group === id
                  ? "border-primary font-medium text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              )}
              onClick={() => select(id)}
              onKeyDown={(event: KeyboardEvent<HTMLButtonElement>) => {
                const index = COMPARE_GROUPS.indexOf(group);
                let next: number;
                if (event.key === "Home") {
                  next = 0;
                } else if (event.key === "End") {
                  next = COMPARE_GROUPS.length - 1;
                } else {
                  // Direction-aware: in a right-to-left strip, ArrowLeft moves
                  // to the NEXT tab. `closestDirection` reads the nearest
                  // `dir` attribute, because jsdom computes no `direction`.
                  const step = horizontalStep(
                    event.key,
                    closestDirection(event.currentTarget),
                  );
                  if (step === 0) {
                    return;
                  }
                  next =
                    (index + step + COMPARE_GROUPS.length) % COMPARE_GROUPS.length;
                }
                event.preventDefault();
                const target = COMPARE_GROUPS[next];
                if (target) {
                  select(target);
                  document.getElementById(`${tablistId}-${target}`)?.focus();
                }
              }}
            >
              {COMPARE_GROUP_LABELS[id]}
            </button>
          ))}
        </div>

        {/*
          A panel element for every group, so each tab's `aria-controls` names
          something; FILLED only once its tab has been shown, and kept mounted
          after that. Two reasons, and the second is the load-bearing one: a
          comparison can hold hundreds of events, so mounting all six at once
          builds a tree nobody asked for — and unmounting the one just left
          would throw away the prompts a clinician explicitly loaded, so coming
          back would spend the audited event reads a second time. Same latch,
          same reason, as the run page's Prompt Inspector tab.
        */}
        {COMPARE_GROUPS.map((id) => (
          <div
            key={id}
            role="tabpanel"
            id={`${tablistId}-${id}-panel`}
            aria-labelledby={`${tablistId}-${id}`}
            hidden={group !== id}
            tabIndex={0}
            className="pt-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          >
            {!shown.has(id) ? null : id === "identity" ? (
              <IdentityGroup result={result} />
            ) : id === "summary" ? (
              <SummaryGroup
                result={result}
                projectId={projectId}
                base={reading.base}
                compared={reading.compared}
              />
            ) : (
              <EventGroup
                group={id}
                alignment={groupAlignment(result, id)}
                projectId={projectId}
                base={reading.base}
                compared={reading.compared}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/** A confounder's published values, where the contract publishes any. */
function ConfounderValues({
  confounder,
}: {
  confounder: PreviewRunComparisonResult["confounders"][number];
}) {
  if (confounder.base === null && confounder.compared === null) {
    // `preview_target` always, by design: the fact that the two differ is the
    // whole of the finding and no identifier is published beside it.
    return null;
  }
  return (
    <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
      <div className="min-w-0">
        <dt className="font-medium text-muted-foreground">Base run</dt>
        <dd className="break-words">
          <ValueList value={confounder.base} />
        </dd>
      </div>
      <div className="min-w-0">
        <dt className="font-medium text-muted-foreground">Compared run</dt>
        <dd className="break-words">
          <ValueList value={confounder.compared} />
        </dd>
      </div>
    </dl>
  );
}

function ValueList({ value }: { value: string | string[] | null }) {
  if (value === null) {
    return <span className="text-muted-foreground">not recorded</span>;
  }
  const values = Array.isArray(value) ? value : [value];
  return (
    <span className="flex flex-wrap gap-x-2 gap-y-1">
      {values.map((item, index) =>
        item === UNATTESTED ? (
          // `unknown` is the literal a serving pod records for an axis it
          // cannot attest. Rendered as words rather than as a value, because
          // it is not one — and it never counts as agreement.
          <span key={`${item}-${index}`} className="text-muted-foreground">
            not attested
          </span>
        ) : (
          <Identifier key={`${item}-${index}`} value={item} />
        ),
      )}
    </span>
  );
}

/** The two runs, as this service knows them without reading a word of their content. */
function RunPair({
  base,
  compared,
  thisRunRef,
}: {
  base: PreviewRunComparisonSide;
  compared: PreviewRunComparisonSide;
  thisRunRef: string;
}) {
  return (
    <div className="grid gap-3 text-sm sm:grid-cols-2">
      <RunFacts side="Base run" run={base} isThisRun={base.previewRunRef === thisRunRef} />
      <RunFacts
        side="Compared run"
        run={compared}
        isThisRun={compared.previewRunRef === thisRunRef}
      />
      <p className="sr-only">
        {base.previewRunRef === thisRunRef
          ? "The run on this page is the base run."
          : "The run on this page is the compared run."}
      </p>
    </div>
  );
}

function RunFacts({
  side,
  run,
  isThisRun,
}: {
  side: string;
  run: PreviewRunComparisonSide;
  isThisRun: boolean;
}) {
  const version = candidateDisplayVersion(run.dispatch.candidateRevision);
  return (
    <div className="min-w-0 space-y-2 rounded-md border border-border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          {side}
        </p>
        {isThisRun ? <Badge tone="outline">This page</Badge> : null}
      </div>
      <dl className="grid gap-x-4 gap-y-2 text-sm sm:grid-cols-2">
        <Fact label="Candidate">
          {version ?? <span className="text-muted-foreground">not available</span>}
        </Fact>
        <Fact label="Target revision">{run.dispatch.previewTargetRevision}</Fact>
        <Fact label="Capture">{CAPTURE_STATE_LABELS[run.run.captureState]}</Fact>
        <Fact label="Captured at">
          {run.run.capturedAt ? (
            <time dateTime={run.run.capturedAt} dir="ltr">
              {formatDateTime(run.run.capturedAt)}
            </time>
          ) : (
            <span className="text-muted-foreground">not recorded</span>
          )}
        </Fact>
      </dl>
    </div>
  );
}

/**
 * Unalignable events no group can claim, because the contract admits a null
 * key. Reported above the tabs so they cannot be lost between groups.
 */
function UnkeyedUnalignable({
  events,
}: {
  events: readonly PreviewRunUnalignableEvent[];
}) {
  if (events.length === 0) {
    return null;
  }
  return (
    <Notice tone="warning">
      {`${plural(events.length, "event", "events")} could not be placed at a coordinate in either run, so ${events.length === 1 ? "it is" : "they are"} not shown under any group. `}
      {UNALIGNABLE_REASON_SENTENCES.missing_turn_number}
    </Notice>
  );
}

// ── Groups ───────────────────────────────────────────────────────────────────

function EventGroup({
  group,
  alignment,
  projectId,
  base,
  compared,
}: {
  group: CompareGroupId;
  alignment: GroupAlignment;
  projectId: string;
  base: PreviewRunComparisonSide;
  compared: PreviewRunComparisonSide;
}) {
  if (groupIsEmpty(alignment)) {
    return (
      <EmptyState
        title={`Neither run recorded anything under ${COMPARE_GROUP_LABELS[group]}`}
        description="Neither trace holds an event of this kind. That is what both runs recorded; it is not a statement that nothing of this kind happened."
      />
    );
  }

  const counts = alignment.families.reduce(
    (total, row) => ({
      aligned: total.aligned + row.aligned,
      identical: total.identical + row.identical,
      differing: total.differing + row.differing,
      added: total.added + row.added,
      removed: total.removed + row.removed,
      unalignable: total.unalignable + row.unalignable,
    }),
    { aligned: 0, identical: 0, differing: 0, added: 0, removed: 0, unalignable: 0 },
  );

  return (
    <div className="space-y-4">
      <p role="status" className="text-sm text-muted-foreground">
        {describeCounts(counts)}
      </p>
      <AlignmentSections
        label={COMPARE_GROUP_LABELS[group]}
        alignment={alignment}
        projectId={projectId}
        base={base}
        compared={compared}
      />
    </div>
  );
}

/**
 * One slice of the alignment, as cards.
 *
 * Shared by the four event groups and by Summary, which slices the same events
 * by call type rather than by family (#1128 review, finding 3) — a summary's
 * own changed fields, coordinate and on-demand bodies belong under Summary, not
 * only under Prompts.
 */
function AlignmentSections({
  label,
  alignment,
  projectId,
  base,
  compared,
}: {
  /** Names the sections, so two slices on one screen are told apart. */
  label: string;
  alignment: GroupAlignment;
  projectId: string;
  base: PreviewRunComparisonSide;
  compared: PreviewRunComparisonSide;
}) {
  const group = label;
  const changed = alignment.aligned.filter((pair) => pair.outcome === "differs");
  const identical = alignment.aligned.filter((pair) => pair.outcome === "identical");

  return (
    <div className="space-y-4">
      {changed.length > 0 ? (
        <section aria-label={`Changed in ${group}`} className="space-y-3">
          <h4 className="text-sm font-medium text-foreground">
            {OUTCOME_LABELS.changed}
          </h4>
          <ul className="space-y-3">
            {changed.map((pair) => (
              <li key={`${pair.base.eventRef}:${pair.compared.eventRef}`}>
                <ChangedCard
                  pair={pair}
                  projectId={projectId}
                  baseRunRef={base.previewRunRef}
                  comparedRunRef={compared.previewRunRef}
                />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {alignment.added.length > 0 || alignment.removed.length > 0 ? (
        <section
          aria-label={`Held by one run only in ${group}`}
          className="space-y-3"
        >
          <h4 className="text-sm font-medium text-foreground">Held by one run only</h4>
          <ul className="space-y-2">
            {alignment.added.map((item) => (
              <li key={`added:${item.compared.eventRef}`}>
                <OneSidedCard outcome="added" kindLabel={EVENT_KIND_LABELS[item.key.kind]} coordinate={describeAlignmentKey(item.key)} />
              </li>
            ))}
            {alignment.removed.map((item) => (
              <li key={`removed:${item.base.eventRef}`}>
                <OneSidedCard outcome="removed" kindLabel={EVENT_KIND_LABELS[item.key.kind]} coordinate={describeAlignmentKey(item.key)} />
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {alignment.unalignable.length > 0 ? (
        <section
          aria-label={`Not comparable in ${group}`}
          className="space-y-3"
        >
          <h4 className="text-sm font-medium text-foreground">Not comparable</h4>
          <ul className="space-y-2">
            {alignment.unalignable.map((item) => (
              <li
                key={`unalignable:${item.side}:${item.event.eventRef}`}
                className="rounded-md border border-border px-3 py-2 text-sm"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone="outline">
                    {UNALIGNABLE_REASON_LABELS[item.reason]}
                  </Badge>
                  <span className="text-muted-foreground">
                    {item.side === "base" ? "Base run" : "Compared run"}
                    {item.key === null ? null : ` · ${describeAlignmentKey(item.key)}`}
                  </span>
                </div>
                <p className="mt-1 text-muted-foreground">
                  {UNALIGNABLE_REASON_SENTENCES[item.reason]}
                </p>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {identical.length > 0 ? (
        <details className="rounded-md border border-border px-3 py-2 text-sm">
          <summary className="cursor-pointer text-foreground">
            {`${plural(identical.length, "identical pair", "identical pairs")}`}
          </summary>
          <ul className="mt-2 space-y-1">
            {identical.map((pair) => (
              <li key={`identical:${pair.base.eventRef}`} className="text-muted-foreground">
                {`${EVENT_KIND_LABELS[pair.key.kind]} · ${describeAlignmentKey(pair.key)}`}
                {pair.ordering === "reordered" ? ` · ${ORDERING_LABELS.reordered}` : null}
              </li>
            ))}
          </ul>
        </details>
      ) : null}
    </div>
  );
}

function ChangedCard({
  pair,
  projectId,
  baseRunRef,
  comparedRunRef,
}: {
  pair: PreviewRunAlignedEvent;
  projectId: string;
  baseRunRef: string;
  comparedRunRef: string;
}) {
  return (
    <article className="space-y-3 rounded-md border border-warning/50 bg-card p-3 text-sm">
      <header className="flex flex-wrap items-center gap-2">
        <Badge tone={OUTCOME_TONES.changed}>{OUTCOME_LABELS.changed}</Badge>
        <span className="font-medium text-foreground">
          {EVENT_KIND_LABELS[pair.key.kind]}
        </span>
        <span className="text-muted-foreground">{describeAlignmentKey(pair.key)}</span>
        {pair.ordering === "reordered" ? (
          <Badge tone="outline">{ORDERING_LABELS.reordered}</Badge>
        ) : null}
      </header>

      <ul className="space-y-2">
        {pair.differences.map((difference) => (
          <li key={difference.field}>
            <DifferenceRow difference={difference} />
          </li>
        ))}
      </ul>

      {pair.layers !== null && pair.layers !== undefined && pair.layers.length > 0 ? (
        <div className="space-y-1">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            Provenance layers
          </p>
          <ul className="space-y-1">
            {pair.layers.map((layer) => (
              <li key={layer.origin} className="flex flex-wrap items-center gap-2">
                <Badge tone={layer.same ? "outline" : "warning"}>
                  {layer.same ? "Identical" : "Changed"}
                </Badge>
                <span className="text-foreground">{layer.origin}</span>
                <span className="text-muted-foreground">
                  {`${layer.baseSegments} → ${layer.comparedSegments} segments`}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {pair.segments !== null && pair.segments !== undefined && pair.segments.length > 0 ? (
        <details>
          <summary className="cursor-pointer text-foreground">
            {`${plural(pair.segments.length, "prompt segment", "prompt segments")}`}
          </summary>
          <ul className="mt-2 space-y-1">
            {pair.segments.map((segment) => (
              <li
                key={`${segment.origin}:${segment.sourceCoordinate ?? "runtime"}:${segment.occurrence}`}
                className="flex flex-wrap items-center gap-2"
              >
                <Badge
                  tone={
                    segment.presence !== "both"
                      ? "accent"
                      : segment.same === false
                        ? "warning"
                        : "outline"
                  }
                >
                  {segment.presence === "base_only"
                    ? "Base only"
                    : segment.presence === "compared_only"
                      ? "Compared only"
                      : segment.same === false
                        ? "Changed"
                        : "Identical"}
                </Badge>
                <span className="text-foreground">{segment.origin}</span>
                {segment.sourceCoordinate !== null ? (
                  <Identifier value={segment.sourceCoordinate} />
                ) : (
                  <span className="text-muted-foreground">built at runtime</span>
                )}
              </li>
            ))}
          </ul>
        </details>
      ) : null}

      {promptBodiesLoadable(pair) ? (
        <ComparePromptBodies
          projectId={projectId}
          baseRunRef={baseRunRef}
          comparedRunRef={comparedRunRef}
          baseEventRef={pair.base.eventRef}
          comparedEventRef={pair.compared.eventRef}
          coordinate={describeAlignmentKey(pair.key)}
        />
      ) : hasUnopenableBodyDifference(pair) ? (
        // A body differs on an event this build's prompt reader does not
        // accept — a turn's reply, a classification's rationale. Say so rather
        // than offering a control that would spend two audited reads and end on
        // an unreadable-event state (#1128 review, finding 2).
        <p className="text-xs text-muted-foreground">{UNOPENABLE_BODY_SENTENCE}</p>
      ) : null}
    </article>
  );
}

function DifferenceRow({ difference }: { difference: PreviewRunEventDifference }) {
  const withheld = difference.field === WITHHELD_FIELD;
  const base = formatRecordedValue(difference.base);
  const compared = formatRecordedValue(difference.compared);
  return (
    <div className="rounded-md border border-border px-3 py-2">
      <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {FIELD_LABELS[difference.field]}
      </p>
      {withheld ? (
        <p className="mt-1 text-muted-foreground">{WITHHELD_SENTENCE}</p>
      ) : (
        <dl className="mt-1 grid gap-x-6 gap-y-1 sm:grid-cols-2">
          <Fact label="Base">
            <RecordedValue text={base.text} absent={base.absent} />
          </Fact>
          <Fact label="Compared">
            <RecordedValue text={compared.text} absent={compared.absent} />
          </Fact>
        </dl>
      )}
    </div>
  );
}

function RecordedValue({ text, absent }: { text: string; absent: boolean }) {
  if (absent) {
    return <span className="text-muted-foreground">{text}</span>;
  }
  // `dir="auto"` so a Hebrew recorded value reads correctly inside a page
  // running either way; a digest is rendered as a machine identifier, which
  // `Identifier` pins to LTR.
  return /^(?:sha(?:256|512):|omitted:)/i.test(text) ? (
    <Identifier value={text} />
  ) : (
    <span dir="auto" className="[overflow-wrap:anywhere]">
      {text}
    </span>
  );
}

function OneSidedCard({
  outcome,
  kindLabel,
  coordinate,
}: {
  outcome: "added" | "removed";
  kindLabel: string;
  coordinate: string;
}) {
  return (
    <div className="rounded-md border border-border px-3 py-2 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <Badge tone={OUTCOME_TONES[outcome]}>{OUTCOME_LABELS[outcome]}</Badge>
        <span className="font-medium text-foreground">{kindLabel}</span>
        <span className="text-muted-foreground">{coordinate}</span>
      </div>
      <p className="mt-1 text-muted-foreground">
        The other run captured this turn and did not produce this event.
      </p>
    </div>
  );
}

/**
 * The design's "summary output when available": a run that produced a clinical
 * summary holds prompt calls of that type, and the service's per-call-type row
 * says whether the two runs' summaries aligned and whether they differed.
 */
function SummaryGroup({
  result,
  projectId,
  base,
  compared,
}: {
  result: PreviewRunComparisonResult;
  projectId: string;
  base: PreviewRunComparisonSide;
  compared: PreviewRunComparisonSide;
}) {
  const summaries = summaryCallTypes(result);
  const alignment = summaryAlignment(result);
  // By NAME, not by array identity: the row rendered below is the same object
  // today, and a table that silently stopped marking summaries if that ever
  // changed would be worse than one that never marked them.
  const summaryTypes = new Set(summaries.map((row) => row.callType));
  const summaryCallCounts = summaries.reduce(
    (total, row) => ({
      aligned: total.aligned + row.aligned,
      identical: total.identical + row.identical,
      differing: total.differing + row.differing,
      added: total.added + row.added,
      removed: total.removed + row.removed,
      unalignable: total.unalignable + row.unalignable,
    }),
    { aligned: 0, identical: 0, differing: 0, added: 0, removed: 0, unalignable: 0 },
  );
  // The cards include the summary prompt call AND its provenance event. Count
  // exactly that rendered slice rather than the prompt-call-only table below.
  const renderedCounts = {
    aligned: alignment.aligned.length,
    identical: alignment.aligned.filter((pair) => pair.outcome === "identical")
      .length,
    differing: alignment.aligned.filter((pair) => pair.outcome === "differs")
      .length,
    added: alignment.added.length,
    removed: alignment.removed.length,
    unalignable: alignment.unalignable.length,
  };
  if (result.promptCalls.length === 0) {
    return (
      <EmptyState
        title="Neither run recorded a prompt call"
        description="A clinical summary is produced by a prompt call, and neither trace holds one. That is what both runs recorded; it is not a statement that no summary was produced."
      />
    );
  }
  return (
    <div className="space-y-4">
      {summaries.length === 0 ? (
        <Notice tone="warning">
          Neither run recorded a prompt call whose type names a summary. Every
          prompt-call type either run produced is listed below, so a summary
          recorded under a type this build does not recognise is still visible.
        </Notice>
      ) : groupIsEmpty(alignment) ? (
        summaryCallCounts.unalignable > 0 ? (
          <Notice tone="warning">
            {`${plural(summaryCallCounts.unalignable, "summary prompt event", "summary prompt events")} could not be placed at a coordinate, so no summary comparison card can be shown. The comparison warning above and the counts below keep that incomplete evidence visible.`}
          </Notice>
        ) : (
          <EmptyState
            title="Neither run recorded a summary event"
            description="A summary call type is named below, and neither trace holds an event at any summary coordinate. That is what both runs recorded; it is not a statement that no summary was produced."
          />
        )
      ) : (
        <>
          <p role="status" className="text-sm text-muted-foreground">
            {describeCounts(renderedCounts)}
          </p>
          <AlignmentSections
            label="Summary"
            alignment={alignment}
            projectId={projectId}
            base={base}
            compared={compared}
          />
        </>
      )}
      <table className="w-full min-w-0 table-auto text-sm">
        <caption className="sr-only">
          Alignment counts per prompt-call type, as reported by the Editor
          Service
        </caption>
        <thead>
          <tr className="border-b border-border text-left text-xs uppercase tracking-wide text-muted-foreground">
            <th scope="col" className="py-2 pe-3 font-medium">Call type</th>
            <th scope="col" className="py-2 pe-3 font-medium">Aligned</th>
            <th scope="col" className="py-2 pe-3 font-medium">Identical</th>
            <th scope="col" className="py-2 pe-3 font-medium">Changed</th>
            <th scope="col" className="py-2 pe-3 font-medium">Only compared</th>
            <th scope="col" className="py-2 pe-3 font-medium">Only base</th>
            <th scope="col" className="py-2 font-medium">Not comparable</th>
          </tr>
        </thead>
        <tbody>
          {result.promptCalls.map((row) => (
            <tr key={row.callType} className="border-b border-border last:border-0">
              <th scope="row" className="py-2 pe-3 text-start font-normal text-foreground">
                <span dir="auto">{row.callType}</span>
                {summaryTypes.has(row.callType) ? (
                  <Badge tone="accent" className="ms-2">
                    Summary
                  </Badge>
                ) : null}
              </th>
              <td className="py-2 pe-3">{row.aligned}</td>
              <td className="py-2 pe-3">{row.identical}</td>
              <td className="py-2 pe-3">{row.differing}</td>
              <td className="py-2 pe-3">{row.added}</td>
              <td className="py-2 pe-3">{row.removed}</td>
              <td className="py-2">{row.unalignable}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The six attested identity axes and the synthetic input script.
 *
 * `candidate_content_hash` is marked as the experiment's VARIABLE rather than
 * one of its controls, because a difference there is the thing an A/B
 * comparison is FOR — and a clinician reading six rows of "different" without
 * that distinction would read the intended change as a fault.
 */
function IdentityGroup({ result }: { result: PreviewRunComparisonResult }) {
  return (
    <div className="space-y-4">
      <section aria-label="Synthetic input script" className="space-y-2">
        <h4 className="text-sm font-medium text-foreground">Synthetic input script</h4>
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Badge
            tone={result.inputScript.relation === "identical" ? "success" : "warning"}
          >
            {INPUT_SCRIPT_RELATION_LABELS[result.inputScript.relation]}
          </Badge>
          <p className="min-w-0 flex-1 text-muted-foreground">
            {INPUT_SCRIPT_RELATION_SENTENCES[result.inputScript.relation]}
          </p>
        </div>
        <dl className="grid gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
          <Fact label="Base run">
            {`${plural(result.inputScript.base.turnCount, "turn", "turns")} · `}
            <Identifier value={result.inputScript.base.digest} />
          </Fact>
          <Fact label="Compared run">
            {`${plural(result.inputScript.compared.turnCount, "turn", "turns")} · `}
            <Identifier value={result.inputScript.compared.digest} />
          </Fact>
        </dl>
      </section>

      <section aria-label="Runtime identity axes" className="space-y-2">
        <h4 className="text-sm font-medium text-foreground">Runtime identity</h4>
        <ul className="space-y-2">
          {result.identity.map((axis) => (
            <li
              key={axis.axis}
              className="space-y-1 rounded-md border border-border px-3 py-2 text-sm"
            >
              <div className="flex flex-wrap items-center gap-2">
                <Badge tone={axis.same ? "success" : "warning"}>
                  {axis.same ? "Same" : "Different"}
                </Badge>
                <span className="font-medium text-foreground">
                  {IDENTITY_AXIS_LABELS[axis.axis]}
                </span>
                {axis.axis === CANDIDATE_AXIS ? (
                  <Badge tone="outline">What the comparison varies</Badge>
                ) : (
                  <Badge tone="outline">Control</Badge>
                )}
              </div>
              <dl className="grid gap-x-6 gap-y-1 text-xs sm:grid-cols-2">
                <Fact label="Base">
                  <ValueList value={axis.base} />
                </Fact>
                <Fact label="Compared">
                  <ValueList value={axis.compared} />
                </Fact>
              </dl>
              {axis.base === UNATTESTED || axis.compared === UNATTESTED ? (
                <p className="text-xs text-muted-foreground">
                  A run that could not attest this axis never counts as agreeing
                  with anything on it, including another unattested run: an
                  unverifiable control is not a control.
                </p>
              ) : null}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

/**
 * Why a comparison could not be made.
 *
 * Two arms are this endpoint's own and are written here; every other arm is a
 * run read's and is rendered by the one component that owns those sentences.
 *
 * `refused` (422/400) is a statement about the REQUEST — one locator named
 * twice, or a shape the contract does not accept — and reveals nothing about
 * whether either locator names anything, so it is safe to report plainly.
 * Collapsing it onto "not available" would tell a clinician who made a typo
 * that their run had disappeared.
 *
 * `not_found` is the ONE generic answer, and here it covers TWO runs: either
 * locator may be unknown, may belong to another Project or another deployment
 * environment, or may name a binding its recorded target no longer holds. The
 * service does not say which, by design, and this sentence must not guess —
 * but it does name the possibilities, because "check the other locator" is the
 * only action available and a clinician needs to know that a run from another
 * project is one of the reasons.
 */
function ComparisonFailure({
  failure,
  error,
  onRetry,
}: {
  failure: ReturnType<typeof classifyComparisonFailure>;
  error: unknown;
  onRetry: () => void;
}) {
  if (failure.kind === "refused") {
    return (
      <Notice tone="destructive" role="alert">
        The Editor Service did not accept this request. A run cannot be compared
        with itself, and a locator must be the value a run page’s address
        carries. Nothing was read.
      </Notice>
    );
  }
  if (failure.kind === "not_found") {
    return (
      <EmptyState
        title="One of these runs is not available"
        description="At least one of the two locators names no preview run available to your account in this environment. It may be out of date, it may belong to a different project or deployment, or its preview target may no longer hold it. The Editor Service answers the same way for all of those and does not say which. Check the other run’s locator on its own page."
      />
    );
  }
  return (
    <PreviewRunReadFailure failure={failure} error={error} onRetry={onRetry} />
  );
}
